import { Component, EventEmitter, HostListener, Output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { UserStateService } from '../../../core/services';

/**
 * App-wide loud banner shown while a support agent is impersonating a
 * customer. Reads support-session state directly from UserStateService —
 * the parent only needs to handle the `exit` event.
 *
 * The banner is the single visible affordance for leaving impersonation;
 * the topbar's previous duplicate exit button was removed when this
 * landed (PR 2). Esc is bound globally and gated on no Material dialog
 * being open so it doesn't compete with dialog Esc handlers (e.g. the
 * album-preview shell, which also listens on document:keydown).
 */
@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
  templateUrl: './impersonation-banner.component.html',
  styleUrl: './impersonation-banner.component.scss'
})
export class ImpersonationBannerComponent {
  private readonly userState = inject(UserStateService);
  private readonly dialog = inject(MatDialog);

  @Output() exit = new EventEmitter<void>();

  readonly isVisible = this.userState.isSupportSession;
  readonly customer = this.userState.user;
  readonly admin = this.userState.originalUser;

  readonly customerLabel = computed(() => {
    const u = this.customer();
    if (!u) return '';
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return name ? `${name} <${u.email}>` : u.email;
  });

  readonly adminLabel = computed(() => {
    const u = this.admin();
    if (!u) return '';
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return name || u.email;
  });

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isVisible()) return;
    // Defer to any open Material dialog — its own Esc handler should fire,
    // not ours (otherwise pressing Esc in a dialog would also exit
    // impersonation, which is too eager).
    if (this.dialog.openDialogs.length > 0) return;
    this.exit.emit();
  }

  onExit(): void {
    this.exit.emit();
  }
}
